import React, { useState, useEffect, useCallback } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
  Paper,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ClearIcon from '@mui/icons-material/Clear';
import { useAppSelector, useAppDispatch } from '../hooks/reduxHooks';
import {
  searchCPTExams,
  selectExam,
  setManuallySet,
  clearSearch,
  detectCPTFromTranscript,
  fetchCPTExam,
} from '../store/cptExamSlice';
import { buildCPTLabel, resolveLaterality } from '../services/cptExamService';
import type { CPTExam } from '../services/cptExamService';
import { typography } from '~/theme';

interface CPTExamSelectorProps {
  onSelect?: (exam: CPTExam | null) => void;
  disabled?: boolean;
  detectedModality?: string | null;
  detectedBodyRegion?: string | null;
  detectedConfidence?: number | null;
  detectedMethod?: string | null;
  /** When provided, triggers auto-detection from transcript text */
  autoDetectTranscript?: string | null;
}

const CPTExamSelector: React.FC<CPTExamSelectorProps> = ({
  onSelect,
  disabled = false,
  detectedModality,
  detectedBodyRegion,
  detectedConfidence,
  detectedMethod,
  autoDetectTranscript,
}) => {
  const dispatch = useAppDispatch();
  const { searchResults, searchLoading, selectedExam, manuallySet, detecting, detection, detectionError } = useAppSelector(
    (state) => state.cptExams
  );

  // Resolved detection method:
  // 1. If detection result is available, use it (even after exam is selected)
  // 2. If the prop is non-manual, prefer it (e.g. from a persisted report)
  // 3. Fall back to prop (covers manual and fallback cases)
  const resolvedDetectionMethod = (
    detection?.detection_method
      ? detection.detection_method
      : (detectedMethod && detectedMethod !== 'manual' ? detectedMethod : null)
  ) ?? (manuallySet && !detection ? 'manual' : detectedMethod ?? null);

  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);

  // Debounced search
  useEffect(() => {
    if (!inputValue || inputValue.length < 2) {
      dispatch(clearSearch());
      return;
    }

    const timer = setTimeout(() => {
      dispatch(searchCPTExams({ q: inputValue, limit: 20 }));
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue, dispatch]);

  // Auto-detect CPT from transcript when provided and no manual selection
  useEffect(() => {
    if (
      autoDetectTranscript &&
      autoDetectTranscript.length > 20 &&
      !selectedExam &&
      !manuallySet &&
      !detecting &&
      !detection &&
      !detectionError
    ) {
      console.log('CPT_EXAM_AUTO_DETECT_TRIGGERED | transcript_len=%d', autoDetectTranscript.length);
      dispatch(detectCPTFromTranscript({ transcript: autoDetectTranscript, useLlm: true }));
    }
  }, [autoDetectTranscript, selectedExam, manuallySet, detecting, detection, detectionError, dispatch]);

  // When auto-detection completes, fetch and auto-select the detected exam.
  // manuallySet is included in deps so that a user-initiated clear (manuallySet=true)
  // immediately prevents this effect from re-selecting the previously detected exam.
  useEffect(() => {
    if (detection && !selectedExam && !manuallySet && detection.cpt_code && detection.confidence > 0) {
      console.log(
        'CPT_EXAM_AUTO_DETECTED | cpt_code=%s | exam=%s | confidence=%.2f | method=%s',
        detection.cpt_code, detection.exam_name, detection.confidence, detection.detection_method,
      );
      // Fetch the full CPT exam details from the database
      const detectedLaterality = detection.laterality && detection.laterality !== 'none'
        ? detection.laterality
        : null;
      const detectedContrast = detection.contrast && detection.contrast !== 'none'
        ? detection.contrast
        : null;
      dispatch(fetchCPTExam({ cptCode: detection.cpt_code, examName: detection.exam_name || undefined, bodyRegion: detection.body_region }))
        .unwrap()
        .then((fullExam) => {
          console.log('CPT_EXAM_FETCHED | cpt_code=%s | exam=%s | laterality=%s | contrast=%s', fullExam.cpt_code, fullExam.exam_name, detectedLaterality || fullExam.laterality, detectedContrast || fullExam.contrast);
          // Merge transcript-detected laterality/contrast into the DB exam record
          // (DB records are generic; classification adds per-transcript specifics)
          const merged: CPTExam = {
            ...fullExam,
            laterality: (!fullExam.laterality || fullExam.laterality === 'none') && detectedLaterality
              ? detectedLaterality
              : fullExam.laterality,
            contrast: (!fullExam.contrast || fullExam.contrast === 'none') && detectedContrast
              ? detectedContrast
              : fullExam.contrast,
          };
          dispatch(selectExam(merged));
          onSelect?.(merged);
        })
        .catch((error) => {
          console.error('CPT_EXAM_FETCH_FAILED | cpt_code=%s | error=%s', detection.cpt_code, error);
          // Fallback: create a minimal CPTExam object if fetch fails
          const fallbackExam: CPTExam = {
            id: 0,
            cpt_code: detection.cpt_code,
            exam_name: detection.exam_name,
            modality: detection.modality,
            body_region: detection.body_region,
            subspecialty: null,
            laterality: detectedLaterality,
            contrast: detectedContrast,
            description: null,
            report_template: null,
            is_active: true,
          };
          dispatch(selectExam(fallbackExam));
          onSelect?.(fallbackExam);
        });
    }
  }, [detection, selectedExam, manuallySet, dispatch, onSelect]);

  const handleSelect = useCallback(
    (_event: React.SyntheticEvent, value: CPTExam | null) => {
      dispatch(selectExam(value));
      dispatch(setManuallySet(true));
      if (value) {
        console.log('CPT_EXAM_SELECTED | cpt_code=%s | exam=%s | method=manual', value.cpt_code, value.exam_name);
      }
      onSelect?.(value);
    },
    [dispatch, onSelect]
  );

  const handleClear = useCallback(() => {
    dispatch(selectExam(null));
    dispatch(setManuallySet(true));
    setInputValue('');
    onSelect?.(null);
  }, [dispatch, onSelect]);

  const getMethodChip = () => {
    const method = resolvedDetectionMethod;
    if (!method) return null;
    const color =
      method === 'manual'
        ? 'primary'
        : method === 'rule_based'
          ? 'success'
          : method === 'llm_detected'
            ? 'info'
            : 'default';
    const label =
      method === 'manual'
        ? 'Manual'
        : method === 'rule_based'
          ? 'Auto-detected'
          : method === 'llm_detected'
            ? 'AI-detected'
            : 'Fallback';
    return (
      <Chip
        size="small"
        label={label}
        color={color as 'primary' | 'success' | 'info' | 'default'}
        icon={method === 'manual' ? <LocalHospitalIcon /> : <AutoFixHighIcon />}
        sx={{ ml: 1 }}
      />
    );
  };

  const confidence = detectedConfidence ?? (detection?.confidence ?? null);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        mb: 1,
        backgroundColor: selectedExam ? 'success.50' : 'background.default',
        borderColor: selectedExam ? 'success.light' : 'divider',
        transition: 'all 0.3s ease',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <LocalHospitalIcon sx={{ fontSize: 18, color: selectedExam ? 'success.main' : 'text.secondary', mr: 0.5 }} />
        <Typography variant="subtitle2" color={selectedExam ? 'success.dark' : 'text.secondary'} fontWeight={typography.label.fontWeight}>
          CPT Exam Details
        </Typography>
        {getMethodChip()}
        {confidence != null && confidence > 0 && (
          <Tooltip title={`Detection confidence: ${(confidence * 100).toFixed(0)}%`}>
            <Chip
              size="small"
              label={`${(confidence * 100).toFixed(0)}%`}
              color={confidence >= 0.7 ? 'success' : 'warning'}
              variant="outlined"
              sx={{ ml: 0.5, height: 20, fontSize: typography.caption.fontSize }}
            />
          </Tooltip>
        )}
        {detecting && (
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, gap: 0.5 }}>
            <CircularProgress size={12} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: typography.caption.fontSize }}>
              Detecting...
            </Typography>
          </Box>
        )}
        {selectedExam && !disabled && (
          <Tooltip title="Clear selection">
            <IconButton size="small" onClick={handleClear} sx={{ ml: 'auto', p: 0.25 }}>
              <ClearIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Search input — always shown so user can search/change CPT code */}
      <Autocomplete
          size="small"
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          value={null}
          onChange={handleSelect}
          inputValue={inputValue}
          onInputChange={(_e, val) => setInputValue(val)}
          options={searchResults}
          loading={searchLoading}
          disabled={disabled}
          getOptionLabel={(option) => buildCPTLabel(option)}
          isOptionEqualToValue={(option, value) =>
            option.cpt_code === value.cpt_code
          }
          renderOption={(props, option) => (
            <li {...props} key={option.cpt_code}>
              <Box sx={{ py: 0.25 }}>
                <Typography variant="body2" fontWeight={typography.label.fontWeight} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {option.cpt_code} — {option.exam_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.modality} · {option.body_region}
                  {option.subspecialty ? ` · ${option.subspecialty}` : ''}
                  {option.laterality ? ` · ${option.laterality}` : ''}
                  {option.contrast && option.contrast !== 'none' ? ` · ${option.contrast.replace(/_/g, ' ')}` : ''}
                </Typography>
                {option.description && (
                  <Typography variant="caption" color="text.disabled" display="block" sx={{ fontSize: typography.caption.fontSize }}>
                    {option.description}
                  </Typography>
                )}
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search by CPT code, exam name, or body region…"
              InputProps={{
                ...params.InputProps,
                startAdornment: <SearchIcon sx={{ fontSize: 18, color: 'text.disabled', mr: 0.5 }} />,
                endAdornment: (
                  <>
                    {searchLoading ? (
                      <CircularProgress color="inherit" size={18} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText={
            inputValue.length < 2
              ? 'Type at least 2 characters to search'
              : 'No CPT exams found'
          }
          sx={{
            '& .MuiAutocomplete-listbox': {
              '& li': { whiteSpace: 'normal' },
            },
          }}
        />

      {/* Show auto-detected info when no manual selection */}
      {!selectedExam && !detecting && detectedModality && (
        <Box sx={{ mt: 0.5, p: 1, bgcolor: 'info.50', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Detected: {detectedModality}
            {detectedBodyRegion ? ` · ${detectedBodyRegion}` : ''}
          </Typography>
        </Box>
      )}

      {/* Compact selected exam summary — full details shown in study fields */}
      {selectedExam && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
          <Chip
            size="small"
            label={buildCPTLabel(selectedExam)}
            color="success"
            sx={{ fontWeight: typography.label.fontWeight, fontSize: typography.caption.fontSize, maxWidth: '100%' }}
          />
          <Chip size="small" label={selectedExam.modality} color="primary" variant="outlined" sx={{ height: 20, fontSize: typography.caption.fontSize }} />
          <Chip size="small" label={selectedExam.body_region} color="primary" variant="outlined" sx={{ height: 20, fontSize: typography.caption.fontSize }} />
          {(() => {
            const lat = resolveLaterality(selectedExam);
            return lat ? (
              <Chip
                size="small"
                label={lat}
                variant="outlined"
                color="warning"
                sx={{ height: 20, fontSize: typography.caption.fontSize }}
              />
            ) : null;
          })()}
          {selectedExam.contrast && selectedExam.contrast !== 'none' && (
            <Chip
              size="small"
              label={selectedExam.contrast.replace(/_/g, ' ')}
              variant="outlined"
              color="info"
              sx={{ height: 20, fontSize: typography.caption.fontSize }}
            />
          )}
        </Box>
      )}
    </Paper>
  );
};

export default CPTExamSelector;
